/**
 * Derived views of catalogs for tables, charts, and talent profiles.
 * Components should import these from `content`, not from catalog files.
 * Catalog order arrays stay local (identity). Titles come from merged dictionaries.
 */
import { getActiveLocale } from './runtime';
import { dictionaries } from './store';
import { order as competencyOrder } from './catalogs/competencies';
import { order as motivationOrder } from './catalogs/motivations';
import { order as personalityOrder } from './catalogs/personality';
import { order as teamStrengthOrder } from './catalogs/teamStrengths';
import { order as salesStrengthOrder } from './catalogs/salesStrengths';
import { aspectOrder, styleOrder } from './catalogs/adept';
import { aliases as reportNameAliases } from './catalogs/reports';
import { languageSelectOptions } from '../constants';
import background102 from '../assets/images/backgrounds/assessmentCardBackground-102.png';
import background201 from '../assets/images/backgrounds/assessmentCardBackground-201.png';
import background301 from '../assets/images/backgrounds/assessmentCardBackground-301.png';
import background302 from '../assets/images/backgrounds/assessmentCardBackground-302.png';
import background344 from '../assets/images/backgrounds/assessmentCardBackground-344.png';
import background370 from '../assets/images/backgrounds/assessmentCardBackground-370.png';
import background390 from '../assets/images/backgrounds/assessmentCardBackground-390.png';

const ASSESSMENT_CARD_BACKGROUNDS = {
  102: background102,
  201: background201,
  301: background301,
  302: background302,
  344: background344,
  370: background370,
  390: background390,
};

function languageTitle(languageNames, code, fallback) {
  if (!languageNames) return fallback;
  return languageNames[code] || languageNames[String(code)] || fallback;
}

function localizeLanguages(languages, languageNames) {
  return (languages || []).map((language) => ({
    code: language.code,
    title: languageTitle(languageNames, language.code, language.title),
  }));
}

/** Identity slugs for assessment taking pages — not translatable. */
const ASSESSMENT_META = {
  102: { slug: 'work-personality-and-behaviour', displayOrder: 1, category: 'personality' },
  201: { slug: 'values-and-motivation', displayOrder: 2, category: 'personality' },
  390: { slug: 'adept15', displayOrder: 3, category: 'adept' },
  302: { slug: 'verbal-reasoning', displayOrder: 4, category: 'reasoning' },
  301: { slug: 'numerical-reasoning', displayOrder: 5, category: 'reasoning' },
  344: { slug: 'deductive-reasoning', displayOrder: 6, category: 'reasoning' },
  370: { slug: 'english', displayOrder: 7, category: 'languages' },
};

function camelFromSnake(value) {
  return String(value).replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function snakeFromCamel(value) {
  return String(value).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function addLookupKeys(map, key, value) {
  if (key == null || value == null) return;
  const asString = String(key);
  map[asString] = value;
  map[camelFromSnake(asString)] = value;
  map[snakeFromCamel(asString)] = value;
}

function enItemFor(enCatalog, item) {
  if (!enCatalog || !item) return null;
  return enCatalog[String(item.id)] || enCatalog[item.id] || enCatalog[item.slug] || null;
}

function addCatalogLookupKeys(map, item, enCatalog, value) {
  addLookupKeys(map, item.slug, value);
  addLookupKeys(map, item.id, value);
  addLookupKeys(map, item.title, value);
  const enItem = enItemFor(enCatalog, item);
  if (enItem?.title && enItem.title !== item.title) {
    addLookupKeys(map, enItem.title, value);
  }
}

function tooltipMap(items, enCatalog, textOf) {
  const map = {};
  items.forEach((item) => {
    const text = textOf(item);
    if (!text) return;
    addCatalogLookupKeys(map, item, enCatalog, text);
  });
  return map;
}

function scaleDescriptionMap(items, enCatalog, entryOf) {
  const map = {};
  items.forEach((item) => {
    addCatalogLookupKeys(map, item, enCatalog, entryOf(item));
  });
  return map;
}

function dictFor(locale) {
  return dictionaries[locale] || dictionaries.en || {};
}

export function buildLookups(locale = 'en') {
  const dict = dictFor(locale);
  const enDict = dictFor('en');
  const competencies = dict.catalogs?.competencies || {};
  const motivations = dict.catalogs?.motivations || {};
  const personality = dict.catalogs?.personality || {};
  const teamStrengths = dict.catalogs?.teamStrengths || {};
  const salesStrengths = dict.catalogs?.salesStrengths || {};
  const adept = dict.catalogs?.adept || { aspects: {}, styles: {} };
  const assessments = dict.catalogs?.assessments || {};
  const assessmentsUi = dict.assessments || {};
  const reports = dict.catalogs?.reports || {};
  const help = dict.help || {};
  const common = dict.common || {};
  const enCompetencies = enDict.catalogs?.competencies || {};
  const enMotivations = enDict.catalogs?.motivations || {};
  const enPersonality = enDict.catalogs?.personality || {};
  const enTeamStrengths = enDict.catalogs?.teamStrengths || {};
  const enSalesStrengths = enDict.catalogs?.salesStrengths || {};
  const enAdept = enDict.catalogs?.adept || { aspects: {}, styles: {} };

  const competencyItems = competencyOrder
    .map((id) => competencies[String(id)])
    .filter(Boolean);
  const motivationItems = motivationOrder
    .map((id) => motivations[String(id)])
    .filter(Boolean);
  const personalityItems = personalityOrder
    .map((id) => personality[id])
    .filter(Boolean);
  const teamStrengthItems = teamStrengthOrder
    .map((id) => teamStrengths[id])
    .filter(Boolean);
  const salesStrengthItems = salesStrengthOrder
    .map((id) => salesStrengths[id])
    .filter(Boolean);
  const adeptAspectItems = aspectOrder
    .map((id) => adept.aspects?.[id])
    .filter(Boolean);
  const adeptStyleItems = styleOrder
    .map((id) => adept.styles?.[id])
    .filter(Boolean);

  const workingEnvironment = motivations['17'];
  const competencyTooltips = tooltipMap(competencyItems, enCompetencies, (item) => item.summary || item.description);
  const motivationTooltips = tooltipMap(motivationItems, enMotivations, (item) => item.summary);
  if (workingEnvironment?.summary) {
    addLookupKeys(motivationTooltips, 'last', workingEnvironment.summary);
  }
  const personalityTooltips = tooltipMap(personalityItems, enPersonality, (item) => item.summary);
  const teamStrengthTooltips = tooltipMap(teamStrengthItems, enTeamStrengths, (item) => item.summary);
  addLookupKeys(teamStrengthTooltips, 'implementation', teamStrengths.initiativeAndResponsibility?.summary);
  const salesStrengthTooltips = tooltipMap(salesStrengthItems, enSalesStrengths, (item) => item.summary);
  const adeptTooltips = tooltipMap(adeptAspectItems, enAdept.aspects, (item) => item.definition);

  const scaleChrome = common.scale || {};
  const competencyScaleDescription = scaleDescriptionMap(competencyItems, enCompetencies, (item) => ({
    title: item.description || item.summary,
    properties: [
      { title: scaleChrome.howToLead, content: item.howToLead || [] },
      { title: scaleChrome.howToDevelop, content: item.howToDevelop || [] },
    ],
  }));

  const motivationScaleDescription = scaleDescriptionMap(motivationItems, enMotivations, (item) => ({
    high: item.high || [],
    low: item.low || [],
    properties: [
      { title: scaleChrome.howToManage, content: item.howToManage || [] },
      { title: scaleChrome.watchOut, content: item.watchOut || [] },
    ],
  }));
  if (workingEnvironment) {
    addLookupKeys(motivationScaleDescription, 'last', motivationScaleDescription[workingEnvironment.slug]);
  }

  const personalityScaleDescription = scaleDescriptionMap(personalityItems, enPersonality, (item) => ({
    title: item.summary,
    sample_items: item.sampleItems || [],
    high: item.high || [],
    low: item.low || [],
  }));

  const teamStrengthScaleDescription = scaleDescriptionMap(teamStrengthItems, enTeamStrengths, (item) => ({
    high: item.high || [],
    low: item.low || [],
  }));
  addLookupKeys(
    teamStrengthScaleDescription,
    'implementation',
    teamStrengthScaleDescription.initiativeAndResponsibility
  );

  const salesStrengthScaleDescription = scaleDescriptionMap(salesStrengthItems, enSalesStrengths, (item) => ({
    high: item.high || [],
    low: item.low || [],
  }));

  const adeptScaleDescription = scaleDescriptionMap(adeptAspectItems, enAdept.aspects, (item) => ({
    high: item.high || [],
    low: item.low || [],
  }));
  const adeptStyleDescriptions = tooltipMap(
    adeptStyleItems,
    enAdept.styles,
    (item) => item.description
  );

  const languageNames = assessmentsUi.languageNames || {};
  const testLanguages = localizeLanguages(languageSelectOptions, languageNames);

  const reportLanguages = {};
  Object.values(reports).forEach((report) => {
    if (Number(report.instrumentId) === 357 || Number(report.id) === 357002) return;
    reportLanguages[String(report.id)] = {
      name: report.name,
      languages: localizeLanguages(report.languages, languageNames),
      report_id: report.id,
      instrument_id: report.instrumentId,
    };
  });

  return {
    competencyTooltips,
    competencyScaleDescription,
    competencyList: competencyItems.map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
    })),
    motivationTooltips,
    motivationScaleDescription,
    motivationList: motivationItems.map((item) => ({
      id: item.id,
      title: item.title,
      description_yes: item.highDescription,
      description_no: item.lowDescription,
    })),
    personalityTooltips,
    personalityScaleDescription,
    teamStrengthTooltips,
    teamStrengthScaleDescription,
    teamStrengthLabels: teamStrengthItems.map((item) => item.title),
    salesStrengthTooltips,
    salesStrengthScaleDescription,
    salesStrengthLabels: salesStrengthItems.map((item) => item.title),
    adeptTooltips,
    adeptScaleDescription,
    adeptAspectLabels: adeptAspectItems.map((item) => item.title),
    adeptAspectItems: adeptAspectItems.map((item) => ({
      id: item.id,
      title: item.title,
    })),
    adeptWorkStyleLabels: adeptStyleItems.map((item) => item.title),
    adeptStyles: adeptStyleItems.map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      aspects: (item.aspectIds || []).map((aspectId) => ({
        id: aspectId,
        title: adept.aspects?.[aspectId]?.title,
      })),
    })),
    assessments,
    reasoningAndSkills: [302, 344, 301, 370]
      .map((id) => assessments[String(id)])
      .filter(Boolean)
      .map((item) => ({
        id: item.id,
        label: item.name,
        description: item.profileDescription,
      })),
    reportNames: reportNameAliases[locale] || reportNameAliases.en || reportNameAliases,
    reportLanguages,
    testLanguages,
    faqItems: help.items || [],
    employeeSizeOptions: (common.employeeSizes || []).map((title) => ({ title })),
    adeptStyleDescriptions,
    assessmentCards: Object.keys(ASSESSMENT_META)
      .map((id) => {
        const item = assessments[String(id)];
        const meta = ASSESSMENT_META[id];
        if (!item || !meta) return null;
        return {
          instrumentId: item.id,
          slug: meta.slug,
          displayOrder: meta.displayOrder,
          category: assessmentsUi.categories?.[meta.category] || meta.category,
          categoryKey: meta.category,
          titleDesktop: item.name,
          subtitleDesktop: item.tooltip,
          shortDescription: item.profileDescription || item.tooltip,
          whatItMeasures: item.profileDescription || item.tooltip,
          duration: assessmentsUi.duration,
          instruction: assessmentsUi.instructionItems,
          pageUrlPath: `/assessment/${meta.slug}`,
          backgroundImages: ASSESSMENT_CARD_BACKGROUNDS[item.id]
            ? [ASSESSMENT_CARD_BACKGROUNDS[item.id]]
            : [],
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.displayOrder - b.displayOrder),
    assessmentBySlug: Object.fromEntries(
      Object.keys(ASSESSMENT_META).map((id) => {
        const item = assessments[String(id)];
        const meta = ASSESSMENT_META[id];
        return [
          meta.slug,
          {
            instrumentId: item?.id,
            slug: meta.slug,
            titleDesktop: item?.name,
            whatItMeasures: item?.profileDescription || item?.tooltip,
            duration: assessmentsUi.duration,
            instruction: assessmentsUi.instructionItems,
            selectTestLanguage: assessmentsUi.selectLanguage,
            rememberChoice: assessmentsUi.rememberChoice,
            startTestButtonText: assessmentsUi.startTest,
          },
        ];
      })
    ),
  };
}

const lookupsByLocale = {};

export function clearLookupsCache(locale) {
  if (locale) {
    delete lookupsByLocale[locale];
    return;
  }
  Object.keys(lookupsByLocale).forEach((key) => {
    delete lookupsByLocale[key];
  });
}

export function getLookups(locale) {
  const loc = locale || getActiveLocale();
  if (!lookupsByLocale[loc]) {
    lookupsByLocale[loc] = buildLookups(loc);
  }
  return lookupsByLocale[loc];
}

export const lookups = new Proxy(
  {},
  {
    get(_target, prop) {
      if (typeof prop === 'symbol' || prop === 'then') return undefined;
      return getLookups()[prop];
    },
    has(_target, prop) {
      return typeof prop === 'string' && prop in getLookups();
    },
    ownKeys() {
      return Reflect.ownKeys(getLookups());
    },
    getOwnPropertyDescriptor(_target, prop) {
      const table = getLookups();
      if (!(prop in table)) return undefined;
      return { configurable: true, enumerable: true, writable: false, value: table[prop] };
    },
  }
);
